import { errorToast } from '../admin/helper/FormHelper'
import { t } from '../i18n/t'

/**
 * Turns whatever axios threw into something the app can reason about (T-017).
 *
 * WHAT WAS WRONG
 *
 * There were 91 copies of `errorToast("Something Went Wrong")` in the service
 * modules. An expired session, a validation failure, a server fault and a dead
 * wifi router were all indistinguishable to the person at the counter — and
 * three of those four have a different thing the user should do next. "Try
 * again" is right for one of them and useless for the other three.
 *
 * This module does the one thing all 91 sites were missing: look at the error
 * before describing it.
 *
 * ON THE MESSAGE TEXT
 *
 * Each kind carries a stable `messageKey` alongside its English text. There is
 * no i18n framework in the project yet — Sprint 2 adds it — and when it arrives
 * these keys are already the thing it needs. Nothing else in the app has to
 * change for the messages to become translatable.
 */

export const ErrorKind = {
    OFFLINE: 'OFFLINE',
    TIMEOUT: 'TIMEOUT',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    VALIDATION: 'VALIDATION',
    CONFLICT: 'CONFLICT',
    RATE_LIMITED: 'RATE_LIMITED',
    SERVER: 'SERVER',
    UNKNOWN: 'UNKNOWN',
}

const MESSAGES = {
    [ErrorKind.OFFLINE]: {
        key: 'errors.offline',
        // Names the likely cause and what to do, because this is the one the
        // shop can actually fix themselves.
        text: 'No connection to the server. Check the internet and try again.',
    },
    [ErrorKind.TIMEOUT]: {
        key: 'errors.timeout',
        text: 'The server took too long to respond. Please try again.',
    },
    [ErrorKind.UNAUTHORIZED]: {
        key: 'errors.unauthorized',
        // The client interceptor is already redirecting to the login screen.
        // This says why, so the redirect does not look like a glitch.
        text: 'Your session has ended. Please log in again.',
    },
    [ErrorKind.FORBIDDEN]: {
        key: 'errors.forbidden',
        // Distinct from the session message on purpose: logging in again will
        // not help, and staff will try it unless told otherwise.
        text: 'You do not have permission to do this.',
    },
    [ErrorKind.NOT_FOUND]: {
        key: 'errors.notFound',
        text: 'That record no longer exists. It may have been deleted.',
    },
    [ErrorKind.VALIDATION]: {
        key: 'errors.validation',
        // Only used when the server says a field is wrong but does not say
        // which. When it does say which, those messages are shown instead.
        text: 'Some of the details are not valid. Please check and try again.',
    },
    [ErrorKind.CONFLICT]: {
        key: 'errors.conflict',
        text: 'Someone else changed this while you were working on it. Reload and try again.',
    },
    [ErrorKind.RATE_LIMITED]: {
        key: 'errors.rateLimited',
        // This one is REACHABLE, not theoretical: the API throttles at 60
        // requests a minute per user (routes/v1.php) and a screen costs five to
        // eight, so a manager clicking quickly through the menu runs into it.
        // As UNKNOWN it said "Something went wrong", and the only sensible
        // response to that message is to try again immediately — which spends
        // another request against the same window and keeps the wall up. So it
        // says the one thing that actually helps: wait.
        text: 'Too many requests just now. Please wait a moment and try again.',
    },
    [ErrorKind.SERVER]: {
        key: 'errors.server',
        // Explicitly not the user's fault, and explicitly worth retrying —
        // which distinguishes it from every other kind here.
        text: 'The server had a problem. This is not your fault — please try again.',
    },
    [ErrorKind.UNKNOWN]: {
        key: 'errors.unknown',
        text: 'Something went wrong.',
    },
}

/**
 * ⚠️ AN EXPIRED TOKEN ON THIS API IS A 400, NOT A 401.
 *
 * API_REFERENCE §1 and §18.8: the auth middleware writes its own response and
 * never reaches the handler that would have made it 401, so a missing or
 * expired token comes back as **HTTP 400 with `error: "error"`**. The backend
 * has decided to leave it that way rather than break clients already keying on
 * 400.
 *
 * That collides with 400's other meaning here — a malformed request — and the
 * collision is not harmless. Classified as VALIDATION, an expired session shows
 * "Some of the details are not valid. Please check and try again." to somebody
 * whose real problem is that they need to log in again, and the session-end
 * redirect never fires. They would sit on a screen that looks fine, correcting
 * fields that are not wrong, with every save failing.
 *
 * So the body is what disambiguates: `error: "error"` is the auth middleware's
 * signature. Nothing else in the documented envelope uses that value — the
 * other codes are "Validation", "NotFound", "Permission" and "Failed".
 */
export function isAuthFailure(status, body) {
    if (status === 401) return true
    return status === 400 && body?.error === 'error'
}

function kindForStatus(status, body) {
    if (isAuthFailure(status, body)) return ErrorKind.UNAUTHORIZED
    if (status === 403) return ErrorKind.FORBIDDEN
    if (status === 404) return ErrorKind.NOT_FOUND
    if (status === 409) return ErrorKind.CONFLICT
    if (status === 429) return ErrorKind.RATE_LIMITED
    if (status === 422 || status === 400) return ErrorKind.VALIDATION
    if (status >= 500) return ErrorKind.SERVER
    return ErrorKind.UNKNOWN
}

/**
 * Normalise one { field: message | [messages] } object into { field: [messages] }.
 *
 * Returns null if nothing in it looked like a message, so the caller can fall
 * through to the next candidate shape rather than treating an empty object as
 * a successful extraction.
 */
function normaliseFieldMap(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null

    const fields = {}
    Object.keys(raw).forEach((field) => {
        const messages = Array.isArray(raw[field]) ? raw[field] : [raw[field]]
        const usable = messages.filter((m) => typeof m === 'string' && m.length > 0)
        if (usable.length) fields[field] = usable
    })

    return Object.keys(fields).length ? fields : null
}

/**
 * Pull out per-field messages if the server sent any.
 *
 * ⚠️ THE BACKEND SENDS THESE IN TWO DIFFERENT PLACES, and both are current.
 *
 * Request-shape failures — a missing field, a string where a number belongs —
 * come from Laravel's own validator, which this API wraps as:
 *
 *     { status: false, message: "Invalid Request", error: { box: ["required"] } }
 *
 * BUSINESS-RULE failures come from the endpoint itself and use the envelope
 * documented in API_REFERENCE §1, where `error` is a machine-readable CODE and
 * the field messages have moved into `data`:
 *
 *     { status: false, message: "The return could not be accepted.",
 *       error: "Validation",
 *       data: { "products.0.box": "Only 2 box remain returnable on this line." } }
 *
 * The second shape is the one that matters most at the counter, because it
 * carries the answer the salesperson needs — how many boxes ARE returnable —
 * and reading only `error` (which is now the bare string "Validation") threw
 * every one of those messages away and toasted a generic line instead.
 *
 * Keys in either shape may be dotted paths — `products.<index>.<field>` — where
 * the index matches the position in the array that was sent, so a screen can
 * attach the message to the row it belongs to.
 *
 * Returns a flat { field: [messages] } or null.
 */
function fieldErrorsFrom(body) {
    // `error` as an OBJECT is the Laravel-validator shape. `errors` is the
    // conventional Laravel key, accepted so nothing breaks if the backend
    // switches to it.
    const fromError = normaliseFieldMap(body?.error) ?? normaliseFieldMap(body?.errors)
    if (fromError) return fromError

    // `error` as a STRING is the envelope's machine-readable code. Only then is
    // `data` a field map rather than a payload — guarding on the code matters,
    // because on a successful response `data` is the actual result and must
    // never be mistaken for a list of complaints.
    if (body?.error === 'Validation') return normaliseFieldMap(body?.data)

    return null
}

/**
 * Classify an error. Pure — no toasts, no dispatch, no navigation — so it can
 * be tested directly and reused by components that want to present a failure
 * themselves rather than have it toasted at them (T-025).
 */
export function classifyApiError(error) {
    const response = error?.response
    const status = response?.status ?? null

    let kind
    /**
     * ⚠️ `!response` IS NOT ENOUGH, AND THE SHOP MET THE GAP.
     *
     * Measured in a real browser, 7 Aug 2026: with the connection cut, the
     * customer list toasted "Something went wrong." — the UNKNOWN message —
     * instead of "No connection to the server. Check the internet and try
     * again." That is the exact distinction this module was written to make,
     * and its unit test asserts it correctly against a HAND-BUILT error object
     * with no `response` key at all.
     *
     * A real failure does not always arrive that tidily. Depending on the
     * adapter and how the request died, an error can carry a `response` whose
     * status is 0 or absent — and a status of 0 fell through every branch of
     * kindForStatus to UNKNOWN. "Something went wrong" is the one message that
     * tells the shop nothing, on the one failure they can actually fix.
     *
     * So the test is "did we get a usable status", not "is there a response
     * object". No status means no answer, which is what OFFLINE means.
     */
    if (!response || !status) {
        // The request never got an answer. axios reports a timeout with code
        // ECONNABORTED and a dead connection as a plain "Network Error".
        const timedOut = error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')
        kind = timedOut ? ErrorKind.TIMEOUT : ErrorKind.OFFLINE
    } else {
        // The body is passed in because on this API the STATUS ALONE cannot
        // tell an expired token from a malformed request — both are 400. See
        // isAuthFailure.
        kind = kindForStatus(status, response?.data)
    }

    const body = response?.data
    const fieldErrors = fieldErrorsFrom(body)

    // A server that returns field errors is describing a validation failure,
    // whatever status it chose to use.
    //
    // BE-04 is now closed and the codes are truthful, so this should no longer
    // ever fire on a 500 — every endpoint routes refusals through one place and
    // a broken business rule is a 422. It stays because it costs nothing and
    // the deployed backend is not always the documented one: the body is the
    // more specific signal, and if the two ever disagree again the body is the
    // one carrying something a person can act on.
    if (fieldErrors
        && kind !== ErrorKind.UNAUTHORIZED
        && kind !== ErrorKind.FORBIDDEN
        // A throttled request was never looked at, so anything field-shaped in
        // its body is not a verdict on what was sent. Telling the user to
        // "check the details" would send them correcting fields that are fine.
        && kind !== ErrorKind.RATE_LIMITED) {
        kind = ErrorKind.VALIDATION
    }

    const serverMessage = typeof body?.message === 'string' && body.message.length
        ? body.message
        : null

    return {
        kind,
        status,
        fieldErrors,
        serverMessage,
        messageKey: MESSAGES[kind].key,
        // Translated at read time, not at classify time — the user may switch
        // language between an error being raised and the screen re-rendering
        // it. The English text in MESSAGES stays as the fallback i18next uses
        // when a key is missing from the active locale (T-023).
        message: t(MESSAGES[kind].key, { defaultValue: MESSAGES[kind].text }),
        // Kept so a caller can still inspect the original if it needs to.
        cause: error,
    }
}

/**
 * Classify, tell the user, and hand the typed error back.
 *
 * The toast stays here for now on purpose. T-025 moves presentation into the
 * screens, where an error can sit next to the field it belongs to instead of
 * floating over the page. Until those screens exist, removing the toast would
 * make failures silent, which is worse than a toast in the wrong place.
 */
export function reportApiError(error) {
    const apiError = classifyApiError(error)

    if (apiError.fieldErrors) {
        // Show what the server actually objected to, one message per problem.
        // The old code did this AND THEN also toasted "Something Went Wrong"
        // unconditionally, so a validation failure produced the real messages
        // plus a meaningless one on top.
        Object.values(apiError.fieldErrors).forEach((messages) => {
            messages.forEach((message) => errorToast(message))
        })
        return apiError
    }

    // A 401 is already being handled by the response interceptor, which clears
    // the session and moves to the login screen. The toast explains why, so the
    // redirect does not look like the app losing its place.
    errorToast(preferredMessage(apiError))

    return apiError
}

/**
 * The server's own message, but only where it is likely to be meant for a
 * person.
 *
 * On a 4xx the server is describing something about this request — "invoice
 * number already used" — and that is far more useful than anything written
 * here. On a 5xx it is describing its own internals, and Laravel in particular
 * will happily return an exception message and a file path. Neither belongs on
 * a screen at the sales counter, so those keep the written text.
 */
function preferredMessage(apiError) {
    const serverKnowsBest = apiError.kind === ErrorKind.VALIDATION
        || apiError.kind === ErrorKind.CONFLICT
        || apiError.kind === ErrorKind.NOT_FOUND

    return serverKnowsBest && apiError.serverMessage
        ? apiError.serverMessage
        : apiError.message
}
